import { TX_TYPES, type TxSummary, type TxType } from '@apm/contracts';
import { statusLevel } from '../status';
import { CANVAS, STATUS, TX_COLOR } from '../theme';
import type { MarkShape } from '../ui';
import { matchesFilter, type RowFilter } from './filter';
import {
  logTicks,
  normalizeRect,
  type Plot,
  plotArea,
  pointTime,
  type Rect,
  timeTickStepMs,
  timeTicks,
  xOfTime,
  yOfDuration,
} from './scale';

/**
 * X-View 캔버스 렌더러 (6회차 결정 2).
 *
 * React 밖의 순수 클래스다. 5분에 수만 점이 들어오므로 SVG·DOM 차트를 쓰지 않는다.
 *
 * 점은 `fillRect`·`Path2D`가 아니라 **픽셀 버퍼에 직접 찍고 `putImageData`로 한 번에 올린다**.
 * 캔버스 도형 호출은 개수에 비례해 래스터화 비용이 붙어서, 3만 점이면 도형 방식은 한 프레임을
 * 훌쩍 넘긴다(실측 p50: fillRect 23.6ms / Path2D 29.1ms / 픽셀 버퍼 16.6ms.
 * 5만 점에서는 55ms 대 16.7ms로 더 벌어진다).
 * 격자도 같은 버퍼에 그려 점 아래로 깔고, 글자·지금선·선택 표시만 도형으로 위에 얹는다.
 *
 * 19회차에 바탕이 흰색이 됐고(결정 14) 점이 **모양으로도** 갈린다(콘솔 검토 D-3).
 * 색만 다르면 2색형 색각에서 유형을 구분할 수 없다. 모양은 5×5 픽셀 판으로 찍는다 —
 * 여전히 대입뿐이라 프레임 비용은 그대로다.
 */

/** 점 색은 유형, 실패·오류는 따로 (6회차 보완 결정 2) */
export const TYPE_COLOR: Record<TxType, string> = TX_COLOR;
/** 4xx·DB 오류. 화면의 `text-fail`과 같은 색 */
export const FAIL_COLOR = STATUS.fail;
/** 5xx·예외. 화면의 `text-error`와 같은 색 */
export const ERROR_COLOR = STATUS.error;

const BACKGROUND = CANVAS.background;
const AXIS = CANVAS.grid;
const AXIS_TEXT = CANVAS.tick;
const NOW_LINE = CANVAS.now;
/** 드래그 사각형. 점 위에 도형으로 얹는다 */
const SELECTION_LINE = CANVAS.selectionLine;
const SELECTION_FILL = CANVAS.selectionFill;
/** CSS 픽셀 기준 점 한 변. 모양을 알아볼 수 있는 가장 작은 크기다 */
const POINT_SIZE = 5;
/** 하루를 넘는 창에서는 눈금에 날짜를 붙인다. 없으면 어제 12시와 오늘 12시가 같아 보인다 */
const TICK_DATE_FROM_MS = 24 * 3_600_000;

/** 유형별 모양. 범례(`ui/Chip`의 `Mark`)와 같은 이름을 쓴다 */
export const TYPE_SHAPE: Record<TxType, MarkShape> = {
  http: 'circle',
  sse: 'diamond',
  mcp: 'square',
  worker: 'cross',
  pdf: 'triangle',
  slow: 'x',
};

/**
 * 5×5 판. 1인 자리에만 픽셀을 찍는다.
 * 점이 작아 색만으로는 못 가르는 자리를 모양이 메운다.
 */
const MASK: Record<MarkShape, readonly number[]> = {
  // ●
  circle: [0, 1, 1, 1, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 1, 1, 1, 0],
  // ■
  square: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  // ▲
  triangle: [0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 1, 1, 1, 0, 0, 1, 1, 1, 0, 1, 1, 1, 1, 1],
  // ◆
  diamond: [0, 0, 1, 0, 0, 0, 1, 1, 1, 0, 1, 1, 1, 1, 1, 0, 1, 1, 1, 0, 0, 0, 1, 0, 0],
  // ✚
  cross: [0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 1, 1, 1, 1, 1, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0],
  // ×
  x: [1, 0, 0, 0, 1, 0, 1, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 1, 0, 1, 0, 0, 0, 1],
};
const MASK_SIZE = 5;

/** 점 색도 목록·상세와 같은 3단계 규칙을 쓴다. 정상일 때만 유형 색이 보인다 */
export function colorOf(row: TxSummary): string {
  const level = statusLevel(row);
  if (level === 'error') return ERROR_COLOR;
  if (level === 'fail') return FAIL_COLOR;
  return TYPE_COLOR[row.txType];
}

/** 모양은 상태와 상관없이 유형을 따른다. 색이 상태, 모양이 유형이다 */
export function shapeOf(row: TxSummary): MarkShape {
  return TYPE_SHAPE[row.txType];
}

/**
 * `#rrggbb` → `Uint32Array`에 그대로 넣을 값.
 * `ImageData`는 바이트가 R,G,B,A 순인데 리틀엔디언에서 32비트로 읽으면 0xAABBGGRR가 된다.
 */
export function packColor(hex: string): number {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return ((255 << 24) | (b << 16) | (g << 8) | r) >>> 0;
}

/** 세로 눈금 숫자. 1000ms부터는 초로 적는다 */
export function formatTick(ms: number): string {
  return ms >= 1_000 ? `${ms / 1_000}s` : `${ms}ms`;
}

/** 가로 눈금 시각. 창이 5분이라 시:분이면 충분하다 */
export function formatTimeTick(time: number, windowMs = 0): string {
  const date = new Date(time);
  const clock = `${`${date.getHours()}`.padStart(2, '0')}:${`${date.getMinutes()}`.padStart(2, '0')}`;
  return windowMs > TICK_DATE_FROM_MS ? `${date.getMonth() + 1}/${date.getDate()} ${clock}` : clock;
}

export interface DrawResult {
  /** 실제로 찍은 점 수. 창 밖과 걸러진 것은 세지 않는다 */
  drawn: number;
  /** 이번 프레임에 걸린 시간(ms) */
  elapsedMs: number;
  /** 찍은 점 중 가장 느린 응답시간. 세로축 자동 확장이 쓴다 (후보 #3) */
  maxDurationMs: number;
}

const PACKED_BACKGROUND = packColor(BACKGROUND);
const PACKED_AXIS = packColor(AXIS);

/**
 * 색과 모양이 함께 한 묶음이 된다 (D-C7). 색은 상태(정상·실패·오류)와 유형이, 모양은 유형이 정하므로
 * 묶음 번호는 `유형 번호 × 3 + 상태 번호`다. 전에는 점마다 `색+모양` 문자열 키를 만들어
 * 실시간 250ms마다 5만 점이면 초당 20만 개의 문자열이 생겼다
 */
const LEVELS = ['ok', 'fail', 'error'] as const;
const BUCKET_COUNT = TX_TYPES.length * LEVELS.length;
const TYPE_INDEX = Object.fromEntries(TX_TYPES.map((type, index) => [type, index])) as Record<
  TxType,
  number
>;
const LEVEL_INDEX = { ok: 0, fail: 1, error: 2 } as const;
/** 묶음 번호 → 찍을 색 값·모양 판. 색은 `colorOf`, 모양은 `shapeOf`와 같은 규칙이다 */
const BUCKET_PACKED: readonly number[] = TX_TYPES.flatMap((type) => [
  packColor(TYPE_COLOR[type]),
  packColor(FAIL_COLOR),
  packColor(ERROR_COLOR),
]);
const BUCKET_MASK: readonly (readonly number[])[] = TX_TYPES.flatMap((type) =>
  LEVELS.map(() => MASK[TYPE_SHAPE[type]]),
);

function bucketOf(row: TxSummary): number {
  return (TYPE_INDEX[row.txType] as number) * LEVELS.length + LEVEL_INDEX[statusLevel(row)];
}

export class XViewRenderer {
  private readonly context: CanvasRenderingContext2D;
  /** 색·모양별 좌표 묶음. 프레임마다 새로 만들지 않고 비워서 재사용한다 */
  private readonly buckets: number[][] = Array.from({ length: BUCKET_COUNT }, () => []);
  /**
   * 묶음을 찍는 순서. 처음 본 순서이고 렌더러가 사는 동안 유지한다. 겹친 점은 나중에 찍은 묶음이
   * 위에 오므로, 문자열 키 `Map`에 넣은 순서와 같게 둔다
   */
  private readonly order: number[] = [];
  private readonly seen = new Uint8Array(BUCKET_COUNT);
  private pixels?: ImageData;
  /** `pixels.data`를 32비트로 본 것. 한 픽셀을 대입 하나로 쓴다 */
  private view?: Uint32Array;
  private ratio = 1;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('그림판을 만들지 못했다');
    this.context = context;
  }

  /** CSS 픽셀 크기와 기기 픽셀 비율을 맞춘다. 매 프레임 부르지 않는다 */
  resize(width: number, height: number, ratio: number): void {
    const deviceWidth = Math.max(1, Math.round(width * ratio));
    const deviceHeight = Math.max(1, Math.round(height * ratio));
    this.ratio = ratio;
    if (this.canvas.width !== deviceWidth) this.canvas.width = deviceWidth;
    if (this.canvas.height !== deviceHeight) this.canvas.height = deviceHeight;
    this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
    this.pixels = this.context.createImageData(deviceWidth, deviceHeight);
    this.view = new Uint32Array(this.pixels.data.buffer);
  }

  draw(rows: readonly TxSummary[], plot: Plot, selection?: Rect, filter?: RowFilter): DrawResult {
    const started = performance.now();
    const context = this.context;
    const view = this.view;
    const pixels = this.pixels;
    if (!view || !pixels) return { drawn: 0, elapsedMs: 0, maxDurationMs: 0 };

    const width = pixels.width;
    const height = pixels.height;
    const ratio = this.ratio;
    const area = plotArea(plot);

    view.fill(PACKED_BACKGROUND);
    this.drawGridLines(plot, view, width, height);

    const buckets = this.buckets;
    for (const points of buckets) points.length = 0;

    let drawn = 0;
    let maxDurationMs = 0;
    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index] as TxSummary;
      if (filter !== undefined && !matchesFilter(row, filter)) continue;
      const x = xOfTime(plot, pointTime(row));
      // 창 왼쪽 밖으로 나간 점은 그리지 않는다. 스토어가 곧 버린다
      if (x < area.left || x > area.right) continue;
      const y = yOfDuration(plot, row.durationMs);
      const bucket = bucketOf(row);
      if (this.seen[bucket] === 0) {
        this.seen[bucket] = 1;
        this.order.push(bucket);
      }
      (buckets[bucket] as number[]).push(x, y);
      if (row.durationMs > maxDurationMs) maxDurationMs = row.durationMs;
      drawn += 1;
    }

    const scale = Math.max(1, Math.round((POINT_SIZE * ratio) / MASK_SIZE));
    const half = ((MASK_SIZE * scale) / 2) | 0;
    for (const bucket of this.order) {
      const points = buckets[bucket] as number[];
      if (points.length === 0) continue;
      const value = BUCKET_PACKED[bucket] as number;
      const mask = BUCKET_MASK[bucket] as readonly number[];
      for (let i = 0; i < points.length; i += 2) {
        const left = ((points[i] as number) * ratio - half) | 0;
        const top = ((points[i + 1] as number) * ratio - half) | 0;
        for (let my = 0; my < MASK_SIZE; my += 1) {
          for (let mx = 0; mx < MASK_SIZE; mx += 1) {
            if (mask[my * MASK_SIZE + mx] === 0) continue;
            for (let dy = 0; dy < scale; dy += 1) {
              const py = top + my * scale + dy;
              if (py < 0 || py >= height) continue;
              const rowStart = py * width;
              for (let dx = 0; dx < scale; dx += 1) {
                const px = left + mx * scale + dx;
                if (px < 0 || px >= width) continue;
                view[rowStart + px] = value;
              }
            }
          }
        }
      }
    }

    context.putImageData(pixels, 0, 0);
    this.drawLabels(plot);

    if (selection) this.drawSelection(selection);

    return { drawn, elapsedMs: performance.now() - started, maxDurationMs };
  }

  /** 드래그 중인 사각형. 픽셀 버퍼를 건드리지 않고 도형으로 위에 얹는다 */
  private drawSelection(selection: Rect): void {
    const context = this.context;
    const box = normalizeRect(selection);
    const width = box.x1 - box.x0;
    const height = box.y1 - box.y0;
    context.fillStyle = SELECTION_FILL;
    context.fillRect(box.x0, box.y0, width, height);
    context.strokeStyle = SELECTION_LINE;
    context.lineWidth = 1;
    context.setLineDash([4, 3]);
    context.strokeRect(box.x0 + 0.5, box.y0 + 0.5, width, height);
    context.setLineDash([]);
  }

  /** 격자는 점 아래에 깔려야 해서 같은 버퍼에 먼저 찍는다. 축에 나란한 1px 선이라 계산이 단순하다 */
  private drawGridLines(plot: Plot, view: Uint32Array, width: number, height: number): void {
    const area = plotArea(plot);
    const ratio = this.ratio;
    const left = Math.max(0, (area.left * ratio) | 0);
    const right = Math.min(width - 1, (area.right * ratio) | 0);
    const top = Math.max(0, (area.top * ratio) | 0);
    const bottom = Math.min(height - 1, (area.bottom * ratio) | 0);

    for (const tick of logTicks(plot)) {
      const y = (yOfDuration(plot, tick) * ratio) | 0;
      if (y < 0 || y >= height) continue;
      const rowStart = y * width;
      for (let x = left; x <= right; x += 1) view[rowStart + x] = PACKED_AXIS;
    }
    for (const tick of timeTicks(plot, timeTickStepMs(plot.windowMs))) {
      const x = (xOfTime(plot, tick) * ratio) | 0;
      if (x < left || x > right) continue;
      for (let y = top; y <= bottom; y += 1) view[y * width + x] = PACKED_AXIS;
    }
  }

  /** 글자와 지금선. 몇 개 안 되므로 도형으로 위에 얹는다 */
  private drawLabels(plot: Plot): void {
    const context = this.context;
    const area = plotArea(plot);

    context.fillStyle = AXIS_TEXT;
    context.font =
      '11px -apple-system, "Segoe UI", "Apple SD Gothic Neo", "Malgun Gothic", sans-serif';

    context.textAlign = 'right';
    context.textBaseline = 'middle';
    for (const tick of logTicks(plot)) {
      context.fillText(formatTick(tick), area.left - 8, yOfDuration(plot, tick));
    }

    context.textAlign = 'center';
    context.textBaseline = 'top';
    for (const tick of timeTicks(plot, timeTickStepMs(plot.windowMs))) {
      context.fillText(formatTimeTick(tick, plot.windowMs), xOfTime(plot, tick), area.bottom + 6);
    }

    // 지금 시각인 오른쪽 끝. 점이 여기서부터 쌓인다
    context.strokeStyle = NOW_LINE;
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(area.right + 0.5, area.top);
    context.lineTo(area.right + 0.5, area.bottom);
    context.stroke();
  }
}
